// Team Color Picker Component

import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Shuffle } from 'lucide-react';

const PRESET_COLORS = [
  '#3B82F6', // Blue
  '#10B981', // Green
  '#F59E0B', // Amber
  '#EF4444', // Red
  '#8B5CF6', // Purple
  '#EC4899', // Pink
  '#06B6D4', // Cyan
  '#6366F1', // Indigo
  '#14B8A6', // Teal
  '#F97316', // Orange
  '#84CC16', // Lime
  '#A855F7', // Violet
];

interface TeamColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  compact?: boolean;
}

export function TeamColorPicker({ value, onChange, compact = false }: Readonly<TeamColorPickerProps>) {
  const [customColor, setCustomColor] = useState(value);

  const handleRandomColor = () => {
    const randomIndex = Math.floor(Math.random() * PRESET_COLORS.length);
    onChange(PRESET_COLORS[randomIndex]);
    setCustomColor(PRESET_COLORS[randomIndex]);
  };

  const handleCustomColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newColor = e.target.value;
    setCustomColor(newColor);
    // Only update if it's a valid hex color
    if (/^#[0-9A-F]{6}$/i.test(newColor)) {
      onChange(newColor);
    }
  };

  const handlePresetClick = (color: string) => {
    onChange(color);
    setCustomColor(color);
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-1">
        {/* Compact Color Swatches */}
        <div className="flex items-center gap-1.5">
          {PRESET_COLORS.map((color) => {
            const isSelected = value.toUpperCase() === color.toUpperCase();
            return (
              <button
                key={color}
                type="button"
                onClick={() => handlePresetClick(color)}
                className={`
                  h-8 w-8 rounded border-2 transition-all flex-shrink-0
                  ${
                    isSelected
                      ? 'border-primary ring-2 ring-primary ring-offset-1'
                      : 'border-border hover:border-primary/50'
                  }
                `}
                style={{ backgroundColor: color }}
                title={color}
              />
            );
          })}
        </div>
        
        {/* Compact Hex Input */}
        <Input
          type="text"
          value={customColor}
          onChange={handleCustomColorChange}
          placeholder="#000000"
          className="font-mono text-xs w-24 h-8"
          maxLength={7}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium">Team Colour</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleRandomColor}
          className="h-7 gap-1.5 text-xs"
        >
          <Shuffle className="h-3 w-3" />
          Random
        </Button>
      </div>

      {/* Preset Colors */}
      <div className="grid grid-cols-6 gap-2">
        {PRESET_COLORS.map((color) => {
          const isSelected = value.toUpperCase() === color.toUpperCase();
          return (
            <button
              key={color}
              type="button"
              onClick={() => handlePresetClick(color)}
              className={`
                h-10 rounded-md border-2 transition-all
                ${
                  isSelected
                    ? 'border-primary ring-2 ring-primary ring-offset-2'
                    : 'border-border hover:border-primary/50'
                }
              `}
              style={{ backgroundColor: color }}
              title={color}
            />
          );
        })}
      </div>

      {/* Custom Hex Input */}
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <Input
            type="text"
            value={customColor}
            onChange={handleCustomColorChange}
            placeholder="#000000"
            className="font-mono text-sm"
            maxLength={7}
          />
        </div>
        <div
          className="h-10 w-10 rounded-md border-2 border-border flex-shrink-0"
          style={{ backgroundColor: value }}
        />
      </div>
      <p className="text-xs text-muted-foreground">
        Enter a hex color code or select from presets
      </p>
    </div>
  );
}
